import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, TextField, IconButton, Chip, CircularProgress, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl,
  InputLabel, Alert, Snackbar, Autocomplete, TablePagination,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  fetchLeaveEntitlements, createLeaveEntitlement, bulkCreateLeaveEntitlement,
  updateLeaveEntitlement, deleteLeaveEntitlement, fetchLeaveTypes, fetchLeavePeriods,
} from '../../store/slices/leaveSlice';
import { fetchEmployees } from '../../store/slices/employeeSlice';
import { AppDispatch, RootState } from '../../store/store';
import { LeaveEntitlementDto, CreateLeaveEntitlement, UpdateLeaveEntitlement, BulkCreateLeaveEntitlement, Employee } from '../../types';
import { leaveApi } from '../../api/leaveApi';
import { toast } from 'react-toastify';

const ALL_ROWS_PAGE_SIZE = 100000;
const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];
const SELECT_ALL_EMPLOYEES_OPTION_ID = '__select_all_employees__';
const TOTAL_DAYS_INPUT_PATTERN = /^\d{0,3}(\.\d{0,2})?$/;
const EMPLOYEE_MULTI_SELECT_SX = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'flex-start',
    maxHeight: 112,
    overflowY: 'auto',
  },
  '& .MuiAutocomplete-tag': {
    maxWidth: 180,
  },
} as const;

type EmployeeSelectAllOption = {
  id: typeof SELECT_ALL_EMPLOYEES_OPTION_ID;
  fullName: string;
  employeeCode: string;
  isSelectAll: true;
};

type EmployeeSelectionOption = Employee | EmployeeSelectAllOption;

const isSelectAllEmployeeOption = (option: EmployeeSelectionOption): option is EmployeeSelectAllOption =>
  option.id === SELECT_ALL_EMPLOYEES_OPTION_ID;

const isAllowedTotalDaysInput = (value: string) => {
  if (!TOTAL_DAYS_INPUT_PATTERN.test(value)) {
    return false;
  }

  const numericValue = Number(value);
  return value === '' || Number.isNaN(numericValue) || numericValue <= 999;
};

const calculateCarryForward = (totalLeaves: number, leavesUsed: number, carryForwardLimit: number): number => {
  if (carryForwardLimit <= 0) {
    return 0;
  }

  const unusedLeaves = totalLeaves - leavesUsed;
  if (unusedLeaves <= 0) {
    return 0;
  }

  return Math.min(unusedLeaves, carryForwardLimit);
};

const LeaveEntitlements: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    entitlements,
    leaveTypes,
    leavePeriods,
    loading,
    entitlementTotalCount,
  } = useSelector((state: RootState) => state.leave);
  const { employees } = useSelector((state: RootState) => state.employees);
  const workingEmployees = useMemo(
    () => employees.filter((e) => e.employmentStatus === 0),
    [employees]
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LeaveEntitlementDto | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [employeeId, setEmployeeId] = useState('');
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [leavePeriodId, setLeavePeriodId] = useState('');
  const [totalDays, setTotalDays] = useState(0);
  const [totalDaysInput, setTotalDaysInput] = useState('');
  const [carryForwardDays, setCarryForwardDays] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);

  const [bulkEmployeeIds, setBulkEmployeeIds] = useState<string[]>([]);
  const [bulkLeaveTypeId, setBulkLeaveTypeId] = useState('');
  const [bulkLeavePeriodId, setBulkLeavePeriodId] = useState('');
  const [bulkTotalDays, setBulkTotalDays] = useState(0);
  const [bulkTotalDaysInput, setBulkTotalDaysInput] = useState('');
  const [bulkCarryForwardDays, setBulkCarryForwardDays] = useState(0);
  const [carryForwardLoading, setCarryForwardLoading] = useState(false);
  const [carryForwardTouched, setCarryForwardTouched] = useState(false);
  const [bulkTotalDaysTouched, setBulkTotalDaysTouched] = useState(false);
  const [bulkCarryForwardTouched, setBulkCarryForwardTouched] = useState(false);

  type BatchRow = CreateLeaveEntitlement & { rowKey: string; totalDaysInput: string };
  const newBatchRow = (): BatchRow => ({
    rowKey: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    employeeId: '',
    leaveTypeId: '',
    leavePeriodId: '',
    totalDays: 0,
    totalDaysInput: '',
    carryForwardDays: 0,
  });
  const [batchRows, setBatchRows] = useState<BatchRow[]>([newBatchRow()]);
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  // 🔥 UPDATED: reusable API call
  const loadEntitlements = () => {
    dispatch(fetchLeaveEntitlements({
      searchTerm: searchTerm?.trim().toLowerCase() || undefined,
      leavePeriodId: periodFilter || undefined,
      leaveTypeId: typeFilter || undefined,
      pageNumber: page + 1,
      pageSize: rowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : rowsPerPage,
    }));
  };

  useEffect(() => {
    dispatch(fetchLeaveTypes());
    dispatch(fetchLeavePeriods());
    //dispatch(fetchEmployees({ filters: { employmentStatus: 0 } }));
    dispatch(fetchEmployees({ page: 1, pageSize: 1000 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    loadEntitlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter, typeFilter, page, rowsPerPage]);

  const handleSearch = () => {
    if (page !== 0) {
      setPage(0);
      return;
    }

    loadEntitlements();
  };
const [totalDaysTouched, setTotalDaysTouched] = useState(false);
  const selectedLeaveType = leaveTypes.find((t) => t.id === leaveTypeId);
  const canCarryForward = Boolean(selectedLeaveType?.isCarryForward);
  const carryForwardLimit = canCarryForward ? selectedLeaveType?.maxCarryForwardDays ?? 0 : 0;
  const isCarryForwardOutOfRange = (
    carryForwardDays < 0 ||
    Boolean(selectedLeaveType && (
      canCarryForward
        ? carryForwardDays > carryForwardLimit
        : carryForwardDays > 0
    ))
  );
  const isCarryForwardInvalid = carryForwardTouched && isCarryForwardOutOfRange;
  const carryForwardValidationMessage = carryForwardDays < 0
    ? 'Carry Forward Days cannot be negative'
    : canCarryForward
      ? `Carry Forward Days must be between 0 and ${carryForwardLimit}`
      : 'Carry Forward Days must be 0 for this leave type';
  const selectedEmployeesForEntitlement = useMemo(
    () => workingEmployees.filter((employee) => selectedEmployeeIds.includes(employee.id)),
    [selectedEmployeeIds, workingEmployees]
  );
  const selectedBulkEmployees = useMemo(
    () => workingEmployees.filter((employee) => bulkEmployeeIds.includes(employee.id)),
    [bulkEmployeeIds, workingEmployees]
  );
  const employeeOptionsWithSelectAll = useMemo<EmployeeSelectionOption[]>(
    () => [
      {
        id: SELECT_ALL_EMPLOYEES_OPTION_ID,
        fullName: 'Select All Employees',
        employeeCode: `${workingEmployees.length} employees`,
        isSelectAll: true,
      },
      ...workingEmployees,
    ],
    [workingEmployees]
  );
  const areAllEmployeesSelected = workingEmployees.length > 0 && selectedEmployeesForEntitlement.length === workingEmployees.length;
  const areAllBulkEmployeesSelected = workingEmployees.length > 0 && selectedBulkEmployees.length === workingEmployees.length;
  const bulkSelectedLeaveType = leaveTypes.find((t) => t.id === bulkLeaveTypeId);
  const canBulkCarryForward = Boolean(bulkSelectedLeaveType?.isCarryForward);
  const bulkCarryForwardLimit = canBulkCarryForward ? bulkSelectedLeaveType?.maxCarryForwardDays ?? 0 : 0;
  const isBulkCarryForwardOutOfRange = (
    bulkCarryForwardDays < 0 ||
    Boolean(bulkSelectedLeaveType && (
      canBulkCarryForward
        ? bulkCarryForwardDays > bulkCarryForwardLimit
        : bulkCarryForwardDays > 0
    ))
  );
  const isBulkCarryForwardInvalid = bulkCarryForwardTouched && isBulkCarryForwardOutOfRange;
  const bulkCarryForwardValidationMessage = bulkCarryForwardDays < 0
    ? 'Carry Forward Days cannot be negative'
    : canBulkCarryForward
      ? `Carry Forward Days must be between 0 and ${bulkCarryForwardLimit}`
      : 'Carry Forward Days must be 0 for this leave type';

  const handleOpenAdd = () => {
    setEditing(null);
    setEmployeeId('');
    setSelectedEmployeeIds([]);
    setLeaveTypeId('');
    setLeavePeriodId('');
    setTotalDays(0);
    setTotalDaysInput('');
    setCarryForwardDays(0);
    setIsActive(true);
    setTotalDaysTouched(false);
    setCarryForwardTouched(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (ent: LeaveEntitlementDto) => {
    setEditing(ent);
    setEmployeeId(ent.employeeId);
    setSelectedEmployeeIds([ent.employeeId]);
    setLeaveTypeId(ent.leaveTypeId);
    setLeavePeriodId(ent.leavePeriodId);
    setTotalDays(ent.totalDays);
    setTotalDaysInput(String(ent.totalDays));
    setCarryForwardDays(ent.carryForwardDays);
    setIsActive(ent.isActive);
    setTotalDaysTouched(false);
    setCarryForwardTouched(false);
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }

    if (!selectedLeaveType) {
      setCarryForwardDays(0);
      return;
    }

    if (!selectedLeaveType.isCarryForward) {
      setCarryForwardDays(0);
      return;
    }

    if (editing) {
      const nextCarryForward = calculateCarryForward(totalDays, editing.usedDays, selectedLeaveType.maxCarryForwardDays);
      if (!carryForwardTouched) {
        setCarryForwardDays(nextCarryForward);
      }
      return;
    }

    const carryForwardEmployeeId = editing
      ? employeeId
      : selectedEmployeeIds.length === 1
        ? selectedEmployeeIds[0]
        : '';

    if (!editing && selectedEmployeeIds.length !== 1) {
      setCarryForwardLoading(false);
      if (!carryForwardTouched) {
        setCarryForwardDays(0);
      }
      return;
    }

    if (!carryForwardEmployeeId || !leavePeriodId) {
      setCarryForwardLoading(false);
      if (!carryForwardTouched) {
        setCarryForwardDays(0);
      }
      return;
    }

    const currentPeriod = leavePeriods.find((p) => p.id === leavePeriodId);
    if (!currentPeriod) {
      setCarryForwardDays(0);
      return;
    }

    let isCancelled = false;

    const loadPreviousCycleCarryForward = async () => {
      setCarryForwardLoading(true);
      try {
        const response = await leaveApi.getLeaveEntitlements({
          employeeId: carryForwardEmployeeId,
          leaveTypeId,
          pageNumber: 1,
          pageSize: 500,
        });

        const currentStart = new Date(currentPeriod.startDate).getTime();
        const previousEntitlement = response.items
          .filter((item) => new Date(item.validTo).getTime() < currentStart)
          .sort((a, b) => new Date(b.validTo).getTime() - new Date(a.validTo).getTime())[0];

        const nextCarryForward = previousEntitlement
          ? calculateCarryForward(previousEntitlement.totalDays, previousEntitlement.usedDays, selectedLeaveType.maxCarryForwardDays)
          : 0;

        if (!isCancelled && !carryForwardTouched) {
          setCarryForwardDays(nextCarryForward);
        }
      } catch {
        if (!isCancelled) {
          setCarryForwardDays(0);
        }
      } finally {
        if (!isCancelled) {
          setCarryForwardLoading(false);
        }
      }
    };

    loadPreviousCycleCarryForward();

    return () => {
      isCancelled = true;
    };
  }, [dialogOpen, editing, employeeId, selectedEmployeeIds, leaveTypeId, leavePeriodId, totalDays, leaveTypes, leavePeriods, selectedLeaveType, carryForwardTouched]);

  const handleSave = async () => {
    try {
      if (totalDays <= 0) {
        setTotalDaysTouched(true);
        setSnackbar({ open: true, message: 'Total Days must be greater than 0', severity: 'error' });
        return;
      }

      if (isCarryForwardOutOfRange) {
        setCarryForwardTouched(true);
        setSnackbar({
          open: true,
          message: carryForwardValidationMessage,
          severity: 'error',
        });
        return;
      }

      if (editing) {
        const data: UpdateLeaveEntitlement = { id: editing.id, totalDays, carryForwardDays, isActive };
        await dispatch(updateLeaveEntitlement({ id: editing.id, data })).unwrap();
        setSnackbar({ open: true, message: 'Entitlement updated', severity: 'success' });
        toast.success('Entitlement updated successfully');
      } else {
        const employeeIdsToSave = selectedEmployeesForEntitlement.map((employee) => employee.id);

        if (employeeIdsToSave.length === 0 || !leaveTypeId || !leavePeriodId) {
          setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
          return;
        }

        if (employeeIdsToSave.length === 1) {
          const data: CreateLeaveEntitlement = {
            employeeId: employeeIdsToSave[0],
            leaveTypeId,
            leavePeriodId,
            totalDays,
            carryForwardDays,
          };
          await dispatch(createLeaveEntitlement(data)).unwrap();
          setSnackbar({ open: true, message: 'Entitlement created', severity: 'success' });
          toast.success('Entitlement created successfully');
        } else {
          const data: BulkCreateLeaveEntitlement = {
            employeeIds: employeeIdsToSave,
            leaveTypeId,
            leavePeriodId,
            totalDays,
            carryForwardDays,
          };
          const result = await dispatch(bulkCreateLeaveEntitlement(data)).unwrap();
          const createdCount = result.count ?? employeeIdsToSave.length;
          setSnackbar({ open: true, message: `${createdCount} entitlements created`, severity: 'success' });
          toast.success(`${createdCount} entitlements created successfully`);
        }
      }
      setDialogOpen(false);
      if (page !== 0 && entitlements.length === 0) {
        setPage(0);
      } else {
        loadEntitlements();
      }
    } catch (err: any) {
      const message = typeof err === 'string' ? err : 'Operation failed';
      setSnackbar({ open: true, message, severity: 'error' });
      toast.error(message);
    }
  };

  const handleBulkSave = async () => {
    if (bulkEmployeeIds.length === 0 || !bulkLeaveTypeId || !bulkLeavePeriodId) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    if (bulkTotalDays <= 0) {
      setBulkTotalDaysTouched(true);
      setSnackbar({ open: true, message: 'Total Days must be greater than 0', severity: 'error' });
      return;
    }

    if (isBulkCarryForwardOutOfRange) {
      setBulkCarryForwardTouched(true);
      setSnackbar({
        open: true,
        message: bulkCarryForwardValidationMessage,
        severity: 'error',
      });
      return;
    }

    const data: BulkCreateLeaveEntitlement = {
      employeeIds: bulkEmployeeIds,
      leaveTypeId: bulkLeaveTypeId,
      leavePeriodId: bulkLeavePeriodId,
      totalDays: bulkTotalDays,
      carryForwardDays: bulkCarryForwardDays,
    };

    try {
      const result = await dispatch(bulkCreateLeaveEntitlement(data)).unwrap();
      setSnackbar({ open: true, message: `${result.count} entitlements created`, severity: 'success' });
      setBulkDialogOpen(false);
      if (page !== 0) {
        setPage(0);
      } else {
        loadEntitlements();
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Bulk create failed', severity: 'error' });
    }
  };

  const handleBatchSave = async () => {
    if (batchRows.length === 0) {
      setSnackbar({ open: true, message: 'Please add at least one entitlement row', severity: 'error' });
      return;
    }

    const normalized = batchRows.map(({ rowKey: _rowKey, totalDaysInput: _totalDaysInput, ...row }) => row);

    const missingRequired = normalized.some((r) => !r.employeeId || !r.leaveTypeId || !r.leavePeriodId);
    if (missingRequired) {
      setSnackbar({ open: true, message: 'Please fill all required fields in every row', severity: 'error' });
      return;
    }

    const invalidTotalDays = normalized.some((r) => !Number.isFinite(r.totalDays) || r.totalDays <= 0);
    if (invalidTotalDays) {
      setSnackbar({ open: true, message: 'Total Days must be greater than 0 in every row', severity: 'error' });
      return;
    }

    const adjusted: CreateLeaveEntitlement[] = [];
    for (const row of normalized) {
      const rowLeaveType = leaveTypes.find((t) => t.id === row.leaveTypeId);
      if (!rowLeaveType) {
        setSnackbar({ open: true, message: 'One or more Leave Types are invalid', severity: 'error' });
        return;
      }

      if (!rowLeaveType.isCarryForward) {
        adjusted.push({ ...row, carryForwardDays: 0 });
        continue;
      }

      const limit = rowLeaveType.maxCarryForwardDays ?? 0;
      if (row.carryForwardDays < 0 || row.carryForwardDays > limit) {
        setSnackbar({ open: true, message: `Carry Forward Days must be between 0 and ${limit}`, severity: 'error' });
        return;
      }

      adjusted.push(row);
    }

    setBatchSubmitting(true);
    try {
      const result = await leaveApi.createLeaveEntitlements(adjusted);
      setSnackbar({
        open: true,
        message: `${result.count} entitlements created${result.skippedCount ? `, ${result.skippedCount} skipped` : ''}`,
        severity: 'success',
      });
      toast.success('Entitlements created successfully');
      setBatchDialogOpen(false);
      setBatchRows([newBatchRow()]);

      if (page !== 0) {
        setPage(0);
      } else {
        loadEntitlements();
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Add Multiple Entitlements failed';
      setSnackbar({ open: true, message, severity: 'error' });
      toast.error(message);
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this entitlement?')) return;
    try {
      await dispatch(deleteLeaveEntitlement(id)).unwrap();
      setSnackbar({ open: true, message: 'Entitlement deleted', severity: 'success' });
      toast.success('Entitlement deleted successfully');
      if (entitlements.length === 1 && page > 0) {
        setPage((prev) => prev - 1);
      } else {
        loadEntitlements();
      }
    } catch (err: any) {
      const message = typeof err === 'string' ? err : 'Delete failed';
      setSnackbar({ open: true, message, severity: 'error' });
      toast.error(message);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const activePeriods = leavePeriods.filter(p => p.isActive);
  const activeTypes = leaveTypes.filter(t => t.isActive);

  return (
    <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Leave Entitlements</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => {
            setBulkEmployeeIds([]);
            setBulkLeaveTypeId('');
            setBulkLeavePeriodId('');
            setBulkTotalDays(0);
            setBulkTotalDaysInput('');
            setBulkTotalDaysTouched(false);
            setBulkCarryForwardDays(0);
            setBulkCarryForwardTouched(false);
            setBulkDialogOpen(true);
          }} sx={{ textTransform: 'none' }}>
            Bulk Assign
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => {
            setBatchRows([newBatchRow()]);
            setBatchDialogOpen(true);
          }} sx={{ textTransform: 'none' }}>
            Add Multiple Entitlements
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ textTransform: 'none' }}>
            Add Entitlement
          </Button>
        </Box>
      </Box>

      {/* 🔥 SEARCH FIXED */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
         <TextField
            size="small"
            placeholder="Search employee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}            
            onKeyUp={(e) => {
              if (e.key !== 'Enter') handleSearch();
            }}
            InputProps={{
              sx: { paddingLeft: 0 }, // 🔥 removed space
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Leave Period</InputLabel>
            <Select value={periodFilter} label="Leave Period" onChange={(e) => {
              setPeriodFilter(e.target.value);
              setPage(0);
            }}>
              <MenuItem value="">All Periods</MenuItem>
              {leavePeriods.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Leave Type</InputLabel>
            <Select value={typeFilter} label="Leave Type" onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(0);
            }}>
              <MenuItem value="">All Types</MenuItem>
              {leaveTypes.map(lt => (
                <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Leave Type</strong></TableCell>
                  <TableCell><strong>Period</strong></TableCell>
                  <TableCell align="center"><strong>Assigned Leave</strong></TableCell>
                  <TableCell align="center"><strong>Carry Fwd</strong></TableCell>
                   <TableCell align="center"><strong>Total</strong></TableCell>
                  <TableCell align="center"><strong>Used</strong></TableCell>
                  <TableCell align="center"><strong>Remaining</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entitlements.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center">No entitlements found</TableCell></TableRow>
                ) : (
                  entitlements.map((ent) => (
                    <TableRow key={ent.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{ent.employeeName}</Typography>
                        <Typography variant="caption" color="text.secondary">{ent.employeeCode}</Typography>
                      </TableCell>
                      <TableCell>{ent.leaveTypeName}</TableCell>
                      <TableCell>{ent.leavePeriodName}</TableCell>
                      <TableCell align="center">{ent.totalDays}</TableCell>
                      <TableCell align="center">{ent.carryForwardDays}</TableCell>
                      <TableCell align="center">{ent.totalDays + ent.carryForwardDays}</TableCell>
                      <TableCell align="center">{ent.usedDays}</TableCell>
                      <TableCell align="center">
                        <Chip label={ent.remainingDays} size="small"
                          color={ent.remainingDays > 0 ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={ent.isActive ? 'Active' : 'Inactive'} size="small"
                          color={ent.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenEdit(ent)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(ent.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={entitlementTotalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </>
        </TableContainer>
      )}

      {/* Single Entitlement Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Entitlement' : 'Add Entitlement'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {!editing && (
              <>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  limitTags={3}
                  sx={EMPLOYEE_MULTI_SELECT_SX}
                  options={employeeOptionsWithSelectAll}
                  getOptionLabel={(option) =>
                    isSelectAllEmployeeOption(option)
                      ? `Select All Employees (${workingEmployees.length})`
                      : `${option.fullName} (${option.employeeCode})`
                  }
                  value={selectedEmployeesForEntitlement}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, newValue, _reason, details) => {
                    if (details?.option && isSelectAllEmployeeOption(details.option)) {
                      const nextIds = areAllEmployeesSelected
                        ? []
                        : workingEmployees.map((employee) => employee.id);

                      setSelectedEmployeeIds(nextIds);
                      setEmployeeId(nextIds.length === 1 ? nextIds[0] : '');
                      setCarryForwardTouched(false);
                      return;
                    }

                    const nextIds = newValue
                      .filter((option) => !isSelectAllEmployeeOption(option))
                      .map((employee) => employee.id);

                    setSelectedEmployeeIds(nextIds);
                    setEmployeeId(nextIds.length === 1 ? nextIds[0] : '');
                    setCarryForwardTouched(false);
                  }}
                  renderOption={(props, option) => {
                    const isSelectAllOption = isSelectAllEmployeeOption(option);
                    const checked = isSelectAllOption
                      ? areAllEmployeesSelected
                      : selectedEmployeeIds.includes(option.id);

                    return (
                      <li {...props}>
                        <Checkbox
                          checked={checked}
                          indeterminate={
                            isSelectAllOption &&
                            selectedEmployeesForEntitlement.length > 0 &&
                            !areAllEmployeesSelected
                          }
                          sx={{ mr: 1, p: 0.5 }}
                        />
                        {isSelectAllOption
                          ? `Select All Employees (${workingEmployees.length})`
                          : `${option.fullName} (${option.employeeCode})`}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employees *"
                      size="small"
                      helperText="Select multiple employees using checkboxes, or choose Select All."
                    />
                  )}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Leave Period *</InputLabel>
                  <Select value={leavePeriodId} label="Leave Period *" onChange={(e) => setLeavePeriodId(e.target.value)}>
                    {activePeriods.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Leave Type *</InputLabel>
                  <Select value={leaveTypeId} label="Leave Type *" onChange={(e) => setLeaveTypeId(e.target.value)}>
                    {activeTypes.map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            <TextField
  size="small"
  fullWidth
  label="Total Days *"
  type="number"
  value={totalDaysInput}
  onBlur={() => {
    setTotalDaysTouched(true);
    if (totalDays <= 0) {
      setTotalDaysInput('');
    }
  }}
  onChange={(e) => {
    const value = e.target.value;

    if (isAllowedTotalDaysInput(value)) {
      const numericValue = Number(value);
      setTotalDaysInput(value);
      setTotalDays(value === '' || Number.isNaN(numericValue) ? 0 : numericValue);
    }
    setTotalDaysTouched(true);
  }}
  inputProps={{
    min: 0.5,
    max: 999,
    step: 0.5,
  }}
  error={totalDaysTouched && (totalDays <= 0 || totalDays > 999)}
  helperText={
    totalDaysTouched && totalDays <= 0
      ? "Total Days must be greater than 0"
      : totalDaysTouched && totalDays > 999
      ? "Maximum 3 digits allowed"
      : ""
  }
/>
            <TextField
              size="small"
              fullWidth
              label="Carry Forward Days"
              type="number"
              value={carryForwardDays}
              onBlur={() => setCarryForwardTouched(true)}
              onChange={(e) => {
                const value = Number(e.target.value);
                setCarryForwardDays(Number.isNaN(value) ? 0 : value);
                setCarryForwardTouched(true);
              }}
              inputProps={{
                min: 0,
                ...(canCarryForward ? { max: carryForwardLimit } : {}),
                step: 0.5,
              }}
              error={isCarryForwardInvalid}
              helperText={
                carryForwardLoading
                  ? 'Calculating carry forward from previous cycle...'
                  : isCarryForwardInvalid
                    ? carryForwardValidationMessage
                    : canCarryForward
                      ? `Maximum allowed from Leave Type: ${carryForwardLimit}`
                      : 'Optional. Keep 0 when there are no carry forward days.'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ textTransform: 'none' }} >
            {loading ? <CircularProgress size={20} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Entitlement Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth sx={{ textTransform: 'none' }}>
        <DialogTitle>Bulk Assign Entitlements</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={3}
              sx={EMPLOYEE_MULTI_SELECT_SX}
              options={employeeOptionsWithSelectAll}
              getOptionLabel={(option) =>
                isSelectAllEmployeeOption(option)
                  ? `Select All Employees (${workingEmployees.length})`
                  : `${option.fullName} (${option.employeeCode})`
              }
              value={selectedBulkEmployees}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue, _reason, details) => {
                if (details?.option && isSelectAllEmployeeOption(details.option)) {
                  setBulkEmployeeIds(
                    areAllBulkEmployeesSelected
                      ? []
                      : workingEmployees.map((employee) => employee.id)
                  );
                  return;
                }

                setBulkEmployeeIds(
                  newValue
                    .filter((option) => !isSelectAllEmployeeOption(option))
                    .map((employee) => employee.id)
                );
              }}
              renderOption={(props, option) => {
                const isSelectAllOption = isSelectAllEmployeeOption(option);
                const checked = isSelectAllOption
                  ? areAllBulkEmployeesSelected
                  : bulkEmployeeIds.includes(option.id);

                return (
                  <li {...props}>
                    <Checkbox
                      checked={checked}
                      indeterminate={
                        isSelectAllOption &&
                        selectedBulkEmployees.length > 0 &&
                        !areAllBulkEmployeesSelected
                      }
                      sx={{ mr: 1, p: 0.5 }}
                    />
                    {isSelectAllOption
                      ? `Select All Employees (${workingEmployees.length})`
                      : `${option.fullName} (${option.employeeCode})`}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employees *"
                  size="small"
                  helperText="Select multiple employees using checkboxes, or choose Select All."
                />
              )}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Leave Period *</InputLabel>
              <Select value={bulkLeavePeriodId} label="Leave Period *" onChange={(e) => setBulkLeavePeriodId(e.target.value)}>
                {activePeriods.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Leave Type *</InputLabel>
              <Select value={bulkLeaveTypeId} label="Leave Type *" onChange={(e) => setBulkLeaveTypeId(e.target.value)}>
                {activeTypes.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" fullWidth label="Total Days" type="number" value={bulkTotalDaysInput}
              onBlur={() => {
                setBulkTotalDaysTouched(true);
                if (bulkTotalDays <= 0) {
                  setBulkTotalDaysInput('');
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (isAllowedTotalDaysInput(value)) {
                  const numericValue = Number(value);
                  setBulkTotalDaysInput(value);
                  setBulkTotalDays(value === '' || Number.isNaN(numericValue) ? 0 : numericValue);
                }
                setBulkTotalDaysTouched(true);
              }} inputProps={{ step: 0.5, min: 0.5, max: 999 }}
              error={bulkTotalDaysTouched && bulkTotalDays <= 0}
              helperText={bulkTotalDaysTouched && bulkTotalDays <= 0 ? 'Total Days must be greater than 0' : ''}
            />
            <TextField
              size="small"
              fullWidth
              label="Carry Forward Days"
              type="number"
              value={bulkCarryForwardDays}
              onBlur={() => setBulkCarryForwardTouched(true)}
              onChange={(e) => {
                const value = Number(e.target.value);
                setBulkCarryForwardDays(Number.isNaN(value) ? 0 : value);
                setBulkCarryForwardTouched(true);
              }}
              inputProps={{
                min: 0,
                ...(canBulkCarryForward ? { max: bulkCarryForwardLimit } : {}),
                step: 0.5,
              }}
              error={isBulkCarryForwardInvalid}
              helperText={
                isBulkCarryForwardInvalid
                  ? bulkCarryForwardValidationMessage
                  : canBulkCarryForward
                    ? `Maximum allowed from Leave Type: ${bulkCarryForwardLimit}`
                    : 'Optional. Keep 0 when there are no carry forward days.'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleBulkSave} disabled={loading} sx={{ textTransform: 'none' }}>
            {loading ? <CircularProgress size={20} /> : `Assign to ${bulkEmployeeIds.length} Employees`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Entitlement Dialog */}
        <Dialog
          open={batchDialogOpen}
          onClose={(event, reason) => {
            if (batchSubmitting) return;

            // ❌ BLOCK OUTSIDE CLICK COMPLETELY
            if (reason === 'backdropClick') return;

            // ✅ allow ESC only (optional)
            if (reason === 'escapeKeyDown') {
              setBatchDialogOpen(false);
            }
          }}
          maxWidth="xl"
          fullWidth
        >
        <DialogTitle>Add Multiple Entitlements</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} mb={1}>
            <Typography variant="body2" color="text.secondary">
              Add multiple entitlements (can mix employees, leave types, and periods) and submit as one request.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setBatchRows((prev) => [...prev, newBatchRow()])}
            >
              Add Row
            </Button>
          </Box>
<TableContainer component={Paper} variant="outlined">
  <Table size="small">

    {/* HEADER FIXED */}
    <TableHead>
      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
        <TableCell colSpan={6}>
          <Box
            display="grid"
            gridTemplateColumns="4fr 3fr 3fr 2fr 2fr 100px"
            gap={2}
          >
            <strong>Employee</strong>
            <strong>Leave Period</strong>
            <strong>Leave Type</strong>
            <strong>Total Days</strong>
            <strong>Carry Forward</strong>
            <strong>Action</strong>
          </Box>
        </TableCell>
      </TableRow>
    </TableHead>

    {/* BODY FIXED */}
    <TableBody>
      {batchRows.map((row) => {
        const rowLeaveType = leaveTypes.find((t) => t.id === row.leaveTypeId);
        const rowCarryForwardLimit = rowLeaveType?.isCarryForward
          ? rowLeaveType.maxCarryForwardDays
          : 0;
        const rowCarryForwardDisabled = !rowLeaveType?.isCarryForward;

        return (
          <TableRow key={row.rowKey} hover>
            <TableCell colSpan={6}>
              <Box
                display="grid"
                gridTemplateColumns="4fr 3fr 3fr 2fr 2fr 100px"
                gap={2}
                alignItems="center"
              >

                {/* Employee */}
                <Autocomplete
                  options={workingEmployees}
                  getOptionLabel={(option) =>
                    `${option.fullName} (${option.employeeCode})`
                  }
                  value={
                    workingEmployees.find((e) => e.id === row.employeeId) || null
                  }
                  onChange={(_, newVal) => {
                    const nextEmployeeId = newVal?.id || '';
                    setBatchRows((prev) =>
                      prev.map((r) =>
                        r.rowKey === row.rowKey
                          ? { ...r, employeeId: nextEmployeeId }
                          : r
                      )
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee"
                      size="small"
                      fullWidth
                    />
                  )}
                />

                {/* Leave Period */}
                <FormControl size="small" fullWidth>
                  <Select
                    value={row.leavePeriodId}
                    onChange={(e) => {
                      const val = String(e.target.value);
                      setBatchRows((prev) =>
                        prev.map((r) =>
                          r.rowKey === row.rowKey
                            ? { ...r, leavePeriodId: val }
                            : r
                        )
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {activePeriods.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Leave Type */}
                <FormControl size="small" fullWidth>
                  <Select
                    value={row.leaveTypeId}
                    onChange={(e) => {
                      const val = String(e.target.value);
                      setBatchRows((prev) =>
                        prev.map((r) =>
                          r.rowKey === row.rowKey
                            ? {
                                ...r,
                                leaveTypeId: val,
                                carryForwardDays: 0
                              }
                            : r
                        )
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {activeTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Total Days */}
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  value={row.totalDaysInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!isAllowedTotalDaysInput(value)) {
                      return;
                    }

                    const numericValue = Number(value);
                    setBatchRows((prev) =>
                      prev.map((r) =>
                        r.rowKey === row.rowKey
                          ? {
                              ...r,
                              totalDaysInput: value,
                              totalDays: value === '' || Number.isNaN(numericValue) ? 0 : numericValue
                            }
                          : r
                      )
                    );
                  }}
                  inputProps={{ step: 0.5, min: 0.5, max: 999 }}
                />

                {/* Carry Forward FIXED */}
                <Box>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    value={row.carryForwardDays}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setBatchRows((prev) =>
                        prev.map((r) =>
                          r.rowKey === row.rowKey
                            ? {
                                ...r,
                                carryForwardDays: Number.isNaN(value)
                                  ? 0
                                  : value
                              }
                            : r
                        )
                      );
                    }}
                    inputProps={{
                      step: 0.5,
                      min: 0,
                      max: rowCarryForwardLimit
                    }}
                    disabled={rowCarryForwardDisabled}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 38
                      }
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.3,
                      ml: 0.5,
                      color: rowCarryForwardDisabled
                        ? 'text.disabled'
                        : 'text.secondary'
                    }}
                  >
                    {rowCarryForwardDisabled
                      ? 'Not applicable'
                      : `Max: ${rowCarryForwardLimit}`}
                  </Typography>
                </Box>

                {/* Action */}
                <IconButton
                  size="small"
                  color="error"
                  onClick={() =>
                    setBatchRows((prev) =>
                      prev.filter((r) => r.rowKey !== row.rowKey)
                    )
                  }
                  disabled={batchRows.length === 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>

              </Box>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>

  </Table>
</TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialogOpen(false)} disabled={batchSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleBatchSave} disabled={batchSubmitting}>
            {batchSubmitting ? <CircularProgress size={20} /> : `Create ${batchRows.length} Entitlements`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveEntitlements;
